import { datadogRum } from '@datadog/browser-rum';
import { makeAudioContext } from '../utils/make-audio-context';
import { pcm16BitToFloat } from '../utils/pcm-16-bit-to-float';
import {
  type AudioIdentity,
  type BrowserAudioFlow,
  safeAudioFlow,
} from './audio-flow';
import '../worklets/pcm-audio-worklet';

// These settings should be widely supported by browsers
const DEFAULT_VIDEO_WIDTH = 640;
const DEFAULT_VIDEO_HEIGHT = 360;

const DEFAULT_VIDEO_BITS_PER_SECOND = 1_000_000;
const DEFAULT_AUDIO_BITS_PER_SECOND = 64_000;

const DEFAULT_MIME_TYPE = 'video/webm';
const PLAYBACK_COMPLETE_DEBOUNCE_MS = 400;

interface StartRecordingOptions {
  chunkDuration: number;
  videoDeviceId?: string;
  audioDeviceId?: string;
  preferredMimeTypes?: string[];
  audioBitsPerSecond?: number;
  videoBitsPerSecond?: number;
  idealVideoWidth?: number;
  idealVideoHeight?: number;
}

interface AudioContextWithSinkId extends AudioContext {
  setSinkId(sinkId: string): Promise<void>;
}

function isSpeakerSelectionBlocked(error: unknown): boolean {
  if (typeof error !== 'object' || error === null) {
    return false;
  }
  const name = 'name' in error ? error.name : undefined;
  const message = 'message' in error ? error.message : undefined;
  return (
    name === 'NotAllowedError' &&
    typeof message === 'string' &&
    message.includes('Permissions-Policy')
  );
}

class AvService {
  private readonly context: AudioContext;

  private readonly destinationNode: GainNode;

  private readonly analyserNode: AnalyserNode;

  private audioStreamSource: MediaStreamAudioSourceNode | null = null;

  private audioBufferSources: AudioBufferSourceNode[] = [];

  private videoTracks: MediaStreamTrack[] = [];

  private audioTracks: MediaStreamTrack[] = [];

  private readonly mediaStreamDestination: MediaStreamAudioDestinationNode;

  private nextPlaybackTime = 0;

  private recorder: MediaRecorder | null = null;

  onvideochunk: ((blob: Blob) => void) | undefined;

  onplaybackcomplete?: () => void;

  private isSettingOutputSpeaker = false;

  private playbackCompleteTimer: ReturnType<typeof setTimeout> | null = null;

  private playbackRecords = new Map<
    AudioBufferSourceNode,
    { bytes: number; start: number; identity?: AudioIdentity; started: boolean }
  >();
  private playbackClockTimer: ReturnType<typeof setInterval> | null = null;
  private lastObservedAudioClock = 0;

  private observePlayback = () => {
    if (this.context.state !== 'running') return;
    if (this.context.currentTime > this.lastObservedAudioClock) {
      this.lastObservedAudioClock = this.context.currentTime;
      this.audioFlow?.playbackProgress();
    }
    for (const record of this.playbackRecords.values()) {
      if (!record.started && this.context.currentTime > record.start) {
        record.started = true;
        this.audioFlow?.playbackProgress(record.identity);
      }
    }
  };

  private stopPlaybackClock() {
    if (this.playbackClockTimer) clearInterval(this.playbackClockTimer);
    this.playbackClockTimer = null;
  }

  constructor(private audioFlow?: BrowserAudioFlow) {
    this.context = makeAudioContext();
    if (audioFlow)
      audioFlow.playbackState = () => ({
        context: this.context.state,
        pending: this.audioBufferSources.length > 0,
        queueMs:
          Math.max(0, this.nextPlaybackTime - this.context.currentTime) * 1000,
      });
    this.mediaStreamDestination = this.context.createMediaStreamDestination();
    this.destinationNode = this.context.createGain();
    this.destinationNode.gain.value = 1;
    this.analyserNode = this.context.createAnalyser();
    this.analyserNode.fftSize = 2048;
    this.analyserNode.smoothingTimeConstant = 0.5;
    this.destinationNode.connect(this.context.destination);
    this.destinationNode.connect(this.mediaStreamDestination);
    this.destinationNode.connect(this.analyserNode);
  }

  getDestinationStream(): MediaStream {
    return this.mediaStreamDestination.stream;
  }

  getAnalyserNode(): AnalyserNode {
    return this.analyserNode;
  }

  async setOutputSpeaker(speakerId: string): Promise<boolean> {
    if (this.isSettingOutputSpeaker) {
      throw new Error('Already setting output speaker');
    }
    this.isSettingOutputSpeaker = true;
    try {
      const audioContext = this.context as AudioContextWithSinkId;
      if (typeof audioContext.setSinkId !== 'function') {
        throw new Error(
          'AudioContext.setSinkId is not supported in this browser.',
        );
      }
      try {
        await audioContext.setSinkId(speakerId);
      } catch (error) {
        if (isSpeakerSelectionBlocked(error)) {
          return false;
        }
        throw new Error(`Failed to set audio output device: ${error}`);
      }
      return true;
    } finally {
      this.isSettingOutputSpeaker = false;
    }
  }

  interrupt() {
    safeAudioFlow(() => {
      if (this.audioFlow) this.audioFlow.playbackSchedulingFailed = false;
      for (const record of this.playbackRecords.values())
        if (this.audioFlow)
          this.audioFlow.counters.outputInterruptedBytes += record.bytes;
      this.playbackRecords.clear();
      this.stopPlaybackClock();
    });
    this.clearPlaybackCompleteTimer();
    for (const source of this.audioBufferSources) {
      source.stop();
    }

    this.audioBufferSources = [];
    this.nextPlaybackTime = 0;
  }

  play(data: Readonly<ArrayBuffer>, identity?: AudioIdentity) {
    if (this.isSettingOutputSpeaker) {
      throw new Error('Output speaker is not set yet');
    }

    this.clearPlaybackCompleteTimer();
    const samples = pcm16BitToFloat(data);

    // Note: Firefox seems to require that the buffer have all of its channel
    // data assigned before assigning the buffer to the buffer source.
    const buffer = this.context.createBuffer(1, samples.length, 24000);
    buffer.getChannelData(0).set(samples);

    const bufferSource = this.context.createBufferSource();
    bufferSource.buffer = buffer;
    bufferSource.connect(this.destinationNode);

    const startTime = Math.max(this.context.currentTime, this.nextPlaybackTime);
    bufferSource.start(startTime);
    bufferSource.onended = () => {
      const index = this.audioBufferSources.indexOf(bufferSource);
      if (index === -1) {
        bufferSource.disconnect();
        return;
      }
      safeAudioFlow(() => {
        this.observePlayback();
        const record = this.playbackRecords.get(bufferSource);
        if (record && this.audioFlow)
          this.audioFlow.counters.outputCompletedBytes += record.bytes;
        this.playbackRecords.delete(bufferSource);
      });
      this.audioBufferSources.splice(index, 1);
      if (this.audioBufferSources.length === 0) {
        this.stopPlaybackClock();
        this.schedulePlaybackComplete();
      }
      bufferSource.disconnect();
    };
    this.audioBufferSources.push(bufferSource);
    this.nextPlaybackTime = startTime + bufferSource.buffer.duration;
    safeAudioFlow(() => {
      if (!this.audioFlow) return;
      this.audioFlow.playbackSchedulingFailed = false;
      this.audioFlow.counters.outputScheduledBytes += data.byteLength;
      this.playbackRecords.set(bufferSource, {
        bytes: data.byteLength,
        start: startTime,
        identity,
        started: false,
      });
      if (!this.playbackClockTimer)
        this.playbackClockTimer = setInterval(
          () => safeAudioFlow(this.observePlayback),
          250,
        );
      this.observePlayback();
    });
  }

  private schedulePlaybackComplete() {
    this.clearPlaybackCompleteTimer();
    this.playbackCompleteTimer = setTimeout(() => {
      this.playbackCompleteTimer = null;
      if (this.audioBufferSources.length === 0) {
        this.onplaybackcomplete?.();
      }
    }, PLAYBACK_COMPLETE_DEBOUNCE_MS);
  }

  private clearPlaybackCompleteTimer() {
    if (this.playbackCompleteTimer) {
      clearTimeout(this.playbackCompleteTimer);
      this.playbackCompleteTimer = null;
    }
  }

  async startRecording({
    chunkDuration = 1000,
    videoDeviceId,
    audioDeviceId,
    preferredMimeTypes,
    audioBitsPerSecond,
    videoBitsPerSecond,
    idealVideoWidth,
    idealVideoHeight,
  }: StartRecordingOptions) {
    // Note: using "max" instead of "ideal" for width and height can cause an
    // error if the device does not support the specified resolution.
    const videoStream = await navigator.mediaDevices.getUserMedia({
      video: {
        ...(videoDeviceId ? { deviceId: videoDeviceId } : {}),
        width: { ideal: idealVideoWidth ?? DEFAULT_VIDEO_WIDTH },
        height: { ideal: idealVideoHeight ?? DEFAULT_VIDEO_HEIGHT },
      },
    });
    const audioStream = await navigator.mediaDevices.getUserMedia({
      audio: audioDeviceId ? { deviceId: audioDeviceId } : true,
    });
    if (audioStream.getAudioTracks().length === 0) {
      const err = new Error('No audio tracks found in user media stream');
      datadogRum.addError(err, {
        selectedMicrophone: audioDeviceId,
      });
      // We might want to throw the error here to stop the recording process
      // but for now, we'll just log it to avoid breaking the user experience.
      console.error(err);
    }
    this.audioStreamSource = this.context.createMediaStreamSource(audioStream);
    this.audioStreamSource.connect(this.mediaStreamDestination);

    this.videoTracks = videoStream.getTracks();
    this.audioTracks = this.mediaStreamDestination.stream.getAudioTracks();

    const combinedStream = new MediaStream([
      ...this.videoTracks,
      ...this.audioTracks,
    ]);

    let supportedMimeType = DEFAULT_MIME_TYPE;
    for (const mimeType of preferredMimeTypes || []) {
      if (MediaRecorder.isTypeSupported(mimeType)) {
        supportedMimeType = mimeType;
        break;
      }
    }

    this.recorder = new MediaRecorder(combinedStream, {
      mimeType: supportedMimeType,
      audioBitsPerSecond: audioBitsPerSecond ?? DEFAULT_AUDIO_BITS_PER_SECOND,
      videoBitsPerSecond: videoBitsPerSecond ?? DEFAULT_VIDEO_BITS_PER_SECOND,
    });
    this.recorder.start(chunkDuration);

    this.recorder.ondataavailable = (event: BlobEvent) => {
      if (event.data && event.data.size > 0) {
        this.onvideochunk?.(event.data);
      }
    };

    return videoStream;
  }

  stopRecording(): Promise<void> {
    this.videoTracks.forEach((track) => {
      track.stop();
    });
    this.audioTracks.forEach((track) => {
      track.stop();
    });

    if (this.audioStreamSource) {
      this.audioStreamSource.disconnect();
      this.audioStreamSource = null;
    }

    const recorder = this.recorder;
    if (recorder) {
      this.recorder = null;
      return new Promise((resolve) => {
        recorder.onstop = () => {
          resolve();
        };
        recorder.stop();
      });
    }

    return Promise.resolve();
  }

  async destroy() {
    this.clearPlaybackCompleteTimer();
    this.interrupt();
    this.stopRecording();

    this.destinationNode.disconnect();
    await this.context.close();
  }
}

export default AvService;
